/**
 * Client Email — templates for this client: Family (Client Letters), School, and Monthly (Monthly Contact category).
 * Opens merged preview + mailto. Family templates always add primary contact to To when they have email.
 * Monthly uses the same template category as the Monthly Contact list page; To line prefers client email
 * then primary/first contact (same idea as batch monthly send), plus template default To.
 * School templates use only template To/CC (e.g. {{ DISTRICT_REF_EMAILS }}).
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { clientAPI, emailAPI, emailTemplateAPI, lookupAPI } from '../services/api';
import {
  CLIENT_LETTERS_CATEGORY,
  SCHOOL_EMAIL_CATEGORY,
  MONTHLY_CONTACT_EMAIL_CATEGORY,
  groupClientLetterTemplates,
  groupMonthlyContactEmailSelectOptions,
  inferMonthlyTemplateRowLanguage,
  normalizeTemplateBaseCode,
  resolveLetterTemplateLangCodeFromClientRow,
} from '../utils/clientLetterTemplateGroups';
import InfoIconButton from './InfoIconButton';
import './ClientEmailTemplates.css';

/** Unique emails, preserve order, case-insensitive. */
function dedupeEmailsPreserveOrder(addresses) {
  const seen = new Set();
  const out = [];
  for (const raw of addresses || []) {
    const e = (raw || '').trim();
    if (!e) continue;
    const key = e.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(e);
  }
  return out;
}

function buildClientLetterMailtoHref({ toList, ccList, subject, body }) {
  const toDeduped = dedupeEmailsPreserveOrder(toList);
  const toSet = new Set(toDeduped.map((e) => e.toLowerCase()));
  const ccDeduped = dedupeEmailsPreserveOrder(ccList).filter((e) => !toSet.has(e.toLowerCase()));
  const toEncoded = toDeduped.map((e) => encodeURIComponent(e)).join(';');
  const params = [];
  if (ccDeduped.length) params.push(`cc=${encodeURIComponent(ccDeduped.join(';'))}`);
  params.push(`subject=${encodeURIComponent(subject || '')}`);
  params.push(`body=${encodeURIComponent(body || '')}`);
  const qs = params.join('&');
  return toEncoded ? `mailto:${toEncoded}?${qs}` : `mailto:?${qs}`;
}

function ClientEmailTemplates() {
  const { client } = useOutletContext() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const suggestHandledKeyRef = useRef(null);
  const [templatesFamily, setTemplatesFamily] = useState([]);
  const [templatesSchool, setTemplatesSchool] = useState([]);
  const [templatesMonthly, setTemplatesMonthly] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showIntroInfo, setShowIntroInfo] = useState(false);
  const [showFamilyHint, setShowFamilyHint] = useState(false);
  const [showSchoolHint, setShowSchoolHint] = useState(false);
  const [showMonthlyHint, setShowMonthlyHint] = useState(false);
  const [modal, setModal] = useState(null);
  const [letterMerge, setLetterMerge] = useState({ status: 'idle' });
  const [emailOpened, setEmailOpened] = useState(false);
  const [contactUpdated, setContactUpdated] = useState(false);
  const [updatingContact, setUpdatingContact] = useState(false);

  const groupedFamily = useMemo(() => groupClientLetterTemplates(templatesFamily), [templatesFamily]);
  const groupedSchool = useMemo(() => groupClientLetterTemplates(templatesSchool), [templatesSchool]);
  /** One row per message (same as Monthly Contact); EN/ES pairs often share a display name but not template_code stem. */
  const groupedMonthly = useMemo(() => {
    const groups = groupMonthlyContactEmailSelectOptions(templatesMonthly);
    return groups.map((g) => ({
      ...g,
      baseCode: g.groupKey,
      codes: (g.rows || []).map((t) => ({
        template_code: t.template_code,
        language: inferMonthlyTemplateRowLanguage(t),
      })),
    }));
  }, [templatesMonthly]);
  const clientLangCode = useMemo(
    () => resolveLetterTemplateLangCodeFromClientRow(client, languages),
    [client, languages]
  );
  const hasSchoolDistrict = Boolean(client?.school_id);

  const closeModal = useCallback(() => {
    setModal(null);
    setEmailOpened(false);
    setContactUpdated(false);
    setUpdatingContact(false);
  }, []);

  useEffect(() => {
    setEmailOpened(false);
    setContactUpdated(false);
    setUpdatingContact(false);
  }, [modal?.chosenCode]);

  useEffect(() => {
    if (!modal) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [modal, closeModal]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [tplRes, langRes] = await Promise.all([
          emailTemplateAPI.getAll(),
          lookupAPI.getLanguages().catch(() => ({ data: { data: [] } })),
        ]);
        if (cancelled) return;
        const list = tplRes.data?.data || tplRes.data || [];
        const arr = Array.isArray(list) ? list : [];
        setTemplatesFamily(arr.filter((t) => (t.category || '') === CLIENT_LETTERS_CATEGORY));
        setTemplatesSchool(arr.filter((t) => (t.category || '') === SCHOOL_EMAIL_CATEGORY));
        setTemplatesMonthly(arr.filter((t) => (t.category || '') === MONTHLY_CONTACT_EMAIL_CATEGORY));
        setLanguages(langRes.data?.data || langRes.data || []);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || 'Failed to load email templates');
          setTemplatesFamily([]);
          setTemplatesSchool([]);
          setTemplatesMonthly([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const openTemplate = useCallback(async (group, chosenCode, chosenLang, emailSection = 'family') => {
    try {
      const res = await emailTemplateAPI.getByCode(chosenCode, chosenLang);
      const detail = res.data?.data || res.data;
      setModal({ group, chosenCode, chosenLang, detail, emailSection });
    } catch (err) {
      console.error('Failed to load template:', err);
      setModal({
        group,
        chosenCode,
        chosenLang,
        detail: null,
        loadError: err.response?.data?.error || err.message,
        emailSection,
      });
    }
  }, []);

  const openGroupModal = useCallback(
    (group, emailSection) => {
      const codes = group.codes || [];
      if (codes.length === 0) return;
      if (codes.length === 1) {
        openTemplate(group, codes[0].template_code, codes[0].language, emailSection);
        return;
      }
      const sorted = [...codes].sort((a, b) => {
        const la = a.language;
        const lb = b.language;
        if (la === 'EN' && lb !== 'EN') return -1;
        if (lb === 'EN' && la !== 'EN') return 1;
        return la.localeCompare(lb);
      });
      const match = clientLangCode ? sorted.find((c) => c.language === clientLangCode) : null;
      const en = sorted.find((c) => c.language === 'EN');
      const chosen = match || en || sorted[0];
      openTemplate(group, chosen.template_code, chosen.language, emailSection);
    },
    [clientLangCode, openTemplate]
  );

  useEffect(() => {
    const suggestBase = searchParams.get('suggestBase');
    if (!suggestBase) {
      suggestHandledKeyRef.current = null;
      return;
    }
    if (loading || groupedFamily.length === 0) return;
    const want = normalizeTemplateBaseCode(suggestBase);
    const group = groupedFamily.find((g) => normalizeTemplateBaseCode(g.baseCode) === want);
    const clearParam = () => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.delete('suggestBase');
          return p;
        },
        { replace: true }
      );
    };
    if (!group) {
      clearParam();
      return;
    }
    const dedupeKey = `${suggestBase}::${group.baseCode}`;
    if (suggestHandledKeyRef.current === dedupeKey) return;
    suggestHandledKeyRef.current = dedupeKey;
    openGroupModal(group, 'family');
    clearParam();
  }, [loading, groupedFamily, searchParams, setSearchParams, openGroupModal]);

  useEffect(() => {
    if (!modal?.detail || !client?.id) {
      setLetterMerge({ status: 'idle' });
      return undefined;
    }
    const templateCode = modal.detail.template_code;
    const language = modal.detail.language || 'EN';
    if (!templateCode) {
      setLetterMerge({ status: 'err', error: 'Template code missing' });
      return undefined;
    }
    let cancelled = false;
    setLetterMerge({ status: 'loading' });
    clientAPI
      .renderEmailLetter(client.id, { template_code: templateCode, language })
      .then((res) => {
        if (cancelled) return;
        const d = res.data?.data;
        setLetterMerge({
          status: 'ok',
          subject: d?.subject ?? '',
          body: d?.body ?? '',
          template_to: Array.isArray(d?.template_to) ? d.template_to : [],
          template_cc: Array.isArray(d?.template_cc) ? d.template_cc : [],
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setLetterMerge({
          status: 'err',
          error: err.response?.data?.error || err.message || 'Failed to merge merge fields',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [modal?.detail?.template_code, modal?.detail?.language, client?.id, modal?.detail]);

  const primaryContactEmail = (() => {
    const contacts = client?.contacts || [];
    const primary = contacts.find((c) => c.is_primary);
    const withEmail = (c) => (c?.contact?.email || '').trim();
    if (primary && withEmail(primary)) return withEmail(primary);
    const firstWithEmail = contacts.find((c) => withEmail(c));
    return firstWithEmail ? withEmail(firstWithEmail) : '';
  })();

  const emailSection = modal?.emailSection || 'family';

  /** Matches monthly-contact batch send: client.email first, else primary / first contact with email. */
  const monthlyContactSeedEmail = useMemo(() => {
    if (client?.email && String(client.email).trim()) return String(client.email).trim();
    return primaryContactEmail || '';
  }, [client?.email, primaryContactEmail]);

  const effectiveToList = useMemo(() => {
    if (letterMerge.status !== 'ok') return [];
    const fromTemplate = letterMerge.template_to || [];
    if (emailSection === 'school') {
      return dedupeEmailsPreserveOrder(fromTemplate);
    }
    if (emailSection === 'monthly') {
      const seed = monthlyContactSeedEmail ? [monthlyContactSeedEmail] : [];
      return dedupeEmailsPreserveOrder([...seed, ...fromTemplate]);
    }
    const primary = primaryContactEmail ? [primaryContactEmail] : [];
    return dedupeEmailsPreserveOrder([...primary, ...fromTemplate]);
  }, [letterMerge, primaryContactEmail, emailSection, monthlyContactSeedEmail]);

  const effectiveCcList = useMemo(() => {
    if (letterMerge.status !== 'ok') return [];
    const toSet = new Set(effectiveToList.map((e) => e.toLowerCase()));
    return dedupeEmailsPreserveOrder(letterMerge.template_cc || []).filter((e) => !toSet.has(e.toLowerCase()));
  }, [letterMerge, effectiveToList]);

  const mailtoHref = useMemo(() => {
    if (letterMerge.status !== 'ok') return null;
    const subj = letterMerge.subject ?? '';
    const body = letterMerge.body ?? '';
    const hasRecipients = effectiveToList.length > 0 || effectiveCcList.length > 0;
    if (!hasRecipients && !subj && !body) return null;
    return buildClientLetterMailtoHref({
      toList: effectiveToList,
      ccList: effectiveCcList,
      subject: subj,
      body,
    });
  }, [letterMerge, effectiveToList, effectiveCcList]);

  const handleUpdateLastContacted = useCallback(async () => {
    if (!client?.id) return;
    setUpdatingContact(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      await clientAPI.update(client.id, {
        last_contacted_at: today,
        last_contact_method: 'email_personal',
      });
      setContactUpdated(true);
    } catch (err) {
      console.error('Failed to update last contacted:', err);
    } finally {
      setUpdatingContact(false);
    }
  }, [client?.id]);

  const toLineWhileLoading =
    emailSection === 'school' ? '—' : emailSection === 'monthly' ? monthlyContactSeedEmail || '—' : primaryContactEmail || '—';

  const allSectionsEmpty =
    !loading && !error && groupedFamily.length === 0 && groupedSchool.length === 0 && groupedMonthly.length === 0;

  return (
    <div className="client-email-templates">
      <div className="client-email-templates-header">
        <div className="client-email-templates-title-row">
          <h1 className="client-email-templates-title">Email Client</h1>
          <InfoIconButton
            onClick={() => setShowIntroInfo((v) => !v)}
            ariaExpanded={showIntroInfo}
            ariaControls="client-email-templates-intro"
            ariaLabel="Show explanation"
            ariaLabelExpanded="Hide explanation"
          />
        </div>
        {showIntroInfo && (
          <div id="client-email-templates-intro" className="client-email-templates-intro">
            <p>
              <strong>Family</strong> templates go to this client&apos;s caregivers. Primary contact is always on To when
              they have an email; template default To/CC add more recipients. Merge fields use this client&apos;s record
              (e.g. <code>{'{{ CL_FIRST }}'}</code>, <code>{'{{ SC_EMAIL }}'}</code>). English and Spanish versions use this
              client&apos;s language preferences when available.
            </p>
            <p>
              <strong>School</strong> templates are for the client&apos;s assigned school district. Set default To/CC using
              merge tags such as <code>{'{{ DISTRICT_REF_EMAILS }}'}</code> or <code>{'{{ DISTRICT_TPC_EMAILS }}'}</code>{' '}
              (Settings → Email Templates, category &quot;School&quot;). The client must have a school district on Client
              Edit.
            </p>
            <p>
              <strong>Monthly</strong> uses the same templates as the <strong>Monthly Contact</strong> list page
              (category &quot;Monthly Contact&quot; in Settings → Email Templates). The preview builds To from this
              client&apos;s email on file when set, otherwise the primary contact, plus any addresses from the template—
              same merge fields as elsewhere for this client.
            </p>
          </div>
        )}
      </div>

      {loading && <p className="client-email-templates-loading">Loading email templates…</p>}
      {error && <p className="client-email-templates-error">{error}</p>}
      {allSectionsEmpty && (
        <p className="client-email-templates-empty">
          No templates are set up for this page. Add templates under Settings → Email Templates with category &quot;Client
          Letters&quot; (Family), &quot;School&quot; (School), or &quot;Monthly Contact&quot; (Monthly).
        </p>
      )}
      {!loading && !error && !allSectionsEmpty && (
        <>
          <div className="client-email-templates-columns">
            <section
              id="client-email-family"
              className="client-email-templates-section"
              aria-labelledby="client-email-family-heading"
            >
              <div className="client-email-templates-section-title-row">
                <h2 id="client-email-family-heading" className="client-email-templates-section-title">
                  Family
                </h2>
                <InfoIconButton
                  onClick={() => setShowFamilyHint((v) => !v)}
                  ariaExpanded={showFamilyHint}
                  ariaControls="client-email-family-hint"
                  ariaLabel="Show Family section explanation"
                  ariaLabelExpanded="Hide Family section explanation"
                />
              </div>
              {showFamilyHint && (
                <p id="client-email-family-hint" className="client-email-templates-section-hint-inline">
                  Email to caregivers using Client Letters templates.
                </p>
              )}
              {groupedFamily.length === 0 ? (
                <p className="client-email-templates-empty client-email-templates-empty-section">
                  No Client Letters templates. Add them under Settings → Email Templates with category &quot;Client
                  Letters&quot;.
                </p>
              ) : (
                <ul className="client-email-templates-list" aria-label="Family email templates">
                  {groupedFamily.map((group) => (
                    <li key={group.baseCode} className="client-email-templates-item">
                      <button
                        type="button"
                        className="client-email-templates-btn"
                        onClick={() => openGroupModal(group, 'family')}
                      >
                        {group.displayName || group.baseCode}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section
              id="client-email-school"
              className="client-email-templates-section"
              aria-labelledby="client-email-school-heading"
            >
              <div className="client-email-templates-section-title-row">
                <h2 id="client-email-school-heading" className="client-email-templates-section-title">
                  School
                </h2>
                <InfoIconButton
                  onClick={() => setShowSchoolHint((v) => !v)}
                  ariaExpanded={showSchoolHint}
                  ariaControls="client-email-school-hint"
                  ariaLabel="Show School section explanation"
                  ariaLabelExpanded="Hide School section explanation"
                />
              </div>
              {showSchoolHint && (
                <p id="client-email-school-hint" className="client-email-templates-section-hint-inline">
                  Email the client&apos;s school district using School category templates.
                </p>
              )}
              {!hasSchoolDistrict && (
                <p className="client-email-templates-school-warning" role="status">
                  This client has no school district assigned. Open <strong>Client Edit</strong> and choose a school
                  district before using these templates so district contacts and merge fields resolve.
                </p>
              )}
              {groupedSchool.length === 0 ? (
                <p className="client-email-templates-empty client-email-templates-empty-section">
                  No School templates. Add them under Settings → Email Templates with category &quot;School&quot;.
                </p>
              ) : (
                <ul className="client-email-templates-list" aria-label="School email templates">
                  {groupedSchool.map((group) => (
                    <li key={group.baseCode} className="client-email-templates-item">
                      <button
                        type="button"
                        className="client-email-templates-btn"
                        onClick={() => openGroupModal(group, 'school')}
                        disabled={!hasSchoolDistrict}
                        title={!hasSchoolDistrict ? 'Assign a school district on Client Edit first' : undefined}
                      >
                        {group.displayName || group.baseCode}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section
              id="client-email-monthly"
              className="client-email-templates-section"
              aria-labelledby="client-email-monthly-heading"
            >
              <div className="client-email-templates-section-title-row">
                <h2 id="client-email-monthly-heading" className="client-email-templates-section-title">
                  Monthly
                </h2>
                <InfoIconButton
                  onClick={() => setShowMonthlyHint((v) => !v)}
                  ariaExpanded={showMonthlyHint}
                  ariaControls="client-email-monthly-hint"
                  ariaLabel="Show Monthly section explanation"
                  ariaLabelExpanded="Hide Monthly section explanation"
                />
              </div>
              {showMonthlyHint && (
                <p id="client-email-monthly-hint" className="client-email-templates-section-hint-inline">
                  Same template library as the Monthly Contact list page. Open in email uses your default mail app with
                  merge fields filled for this client.
                </p>
              )}
              {groupedMonthly.length === 0 ? (
                <p className="client-email-templates-empty client-email-templates-empty-section">
                  No templates in the Monthly Contact category. Add them under Settings → Email Templates with category
                  &quot;Monthly Contact&quot;.
                </p>
              ) : (
                <ul className="client-email-templates-list" aria-label="Monthly email templates">
                  {groupedMonthly.map((group) => (
                    <li key={group.groupKey} className="client-email-templates-item">
                      <button
                        type="button"
                        className="client-email-templates-btn"
                        onClick={() => openGroupModal(group, 'monthly')}
                      >
                        {group.displayName || group.baseCode}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </>
      )}

      {client && (
        <p className="client-email-templates-context">
          Client: {[client.last_name, client.first_name].filter(Boolean).join(', ') || '—'}
          {client.date_of_birth && ` · DOB: ${client.date_of_birth}`}
        </p>
      )}

      {modal && (
        <div className="client-email-templates-modal-backdrop" onClick={closeModal} role="presentation">
          <div className="client-email-templates-modal" onClick={(e) => e.stopPropagation()}>
            <div className="client-email-templates-modal-header">
              <h2>{modal.group?.displayName || modal.chosenCode}</h2>
              <button type="button" className="client-email-templates-modal-close" onClick={closeModal} aria-label="Close">
                ×
              </button>
            </div>
            {modal.loadError && <p className="client-email-templates-modal-error">{modal.loadError}</p>}
            {modal.detail && (
              <>
                <div className="client-email-templates-modal-body">
                  <p>
                    <strong>To:</strong>{' '}
                    {letterMerge.status === 'ok'
                      ? effectiveToList.length > 0
                        ? effectiveToList.join(', ')
                        : '— (add at least one address in your email client)'
                      : toLineWhileLoading}
                  </p>
                  {letterMerge.status === 'ok' && effectiveCcList.length > 0 && (
                    <p>
                      <strong>CC:</strong> {effectiveCcList.join(', ')}
                    </p>
                  )}
                  {letterMerge.status === 'loading' && (
                    <p className="client-email-templates-modal-merge-status" aria-live="polite">
                      Filling merge fields…
                    </p>
                  )}
                  {letterMerge.status === 'err' && (
                    <p className="client-email-templates-modal-error" role="alert">
                      {letterMerge.error}
                    </p>
                  )}
                  {letterMerge.status === 'ok' && (
                    <>
                      <p>
                        <strong>Subject:</strong> {letterMerge.subject || '—'}
                      </p>
                      <div className="client-email-templates-modal-content">
                        <strong>Body:</strong>
                        <pre>{letterMerge.body || '—'}</pre>
                      </div>
                    </>
                  )}
                </div>
                <div className="client-email-templates-modal-actions">
                  {letterMerge.status === 'ok' && mailtoHref && (
                    <a
                      href={mailtoHref}
                      className="client-email-templates-open-email"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        setEmailOpened(true);
                        if (client?.id && modal?.detail?.template_code) {
                          emailAPI.clientLetterOpened(client.id, modal.detail.template_code).catch(() => {});
                        }
                      }}
                    >
                      Open in email
                    </a>
                  )}
                  {letterMerge.status === 'ok' && !mailtoHref && (
                    <span className="client-email-templates-modal-merge-status">
                      Nothing to open (empty recipients, subject, and body).
                    </span>
                  )}
                  <button type="button" className="client-email-templates-modal-close-btn" onClick={closeModal}>
                    Close
                  </button>
                </div>
                {emailOpened && emailSection !== 'school' && (
                  <div className="client-email-templates-record-contact">
                    {!contactUpdated ? (
                      <>
                        <p>Update &quot;Last contacted&quot; for {client?.first_name} {client?.last_name}?</p>
                        <button
                          type="button"
                          className="client-email-templates-record-contact-btn"
                          onClick={handleUpdateLastContacted}
                          disabled={updatingContact}
                        >
                          {updatingContact ? 'Updating…' : 'Yes, update last contact'}
                        </button>
                      </>
                    ) : (
                      <p className="client-email-templates-record-contact-done">✓ Last contacted updated.</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientEmailTemplates;
